import React, { useState, useEffect } from 'react'
import { Modal, Button, Alert, ListGroup, Badge, Form } from 'react-bootstrap'

const sanitizarSerie = (s) => String(s || '')
  .replace(/[\r\n\t]/g, ' ')
  .replace(/[<>&"']/g, '')
  .slice(0, 60)

/**
 * ModalIncluirDependienteIngreso — al marcar un equipo padre en /ingresos/crear,
 * pregunta si incluir también sus dependientes. Ofrece 3 opciones:
 *   - Incluir con mismos accesorios/observación/fotos (recomendado, default).
 *   - Incluir y capturar datos por separado.
 *   - Solo el padre.
 *
 * Spec: openspec/changes/items-adicionales-equipo-dependiente — capability
 * `ingresos-parejas-dependientes`.
 *
 * Props:
 *   show                     boolean
 *   padre                    EquipmentInOt del padre (ya con accesorios/observacion/fotos capturados)
 *   hijos                    EquipmentInOt[] disponibles para ingresar
 *   onIncluirCompartiendo    () => void — incluir hijos heredando datos del padre
 *   onIncluirPorSeparado     () => void — incluir hijos pero abrir modal Accesorios por cada uno
 *   onSoloPadre              () => void — no incluir hijos
 *   onCancelar               () => void — deshacer también el marcado del padre
 */
const ModalIncluirDependienteIngreso = ({
  show,
  padre,
  hijos = [],
  onIncluirCompartiendo,
  onIncluirPorSeparado,
  onSoloPadre,
  onCancelar,
}) => {
  const [compartir, setCompartir] = useState(true)
  useEffect(() => {
    if (show) setCompartir(true)
  }, [show])

  return (
    <Modal show={show} onHide={onCancelar} size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton={false}>
        <Modal.Title>Incluir dependiente(s) al ingreso</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          El equipo <strong>{padre?.snapshot?.item || 'Padre'}</strong> tiene{' '}
          <strong>{hijos.length} dependiente(s)</strong> disponible(s) en esta OT.
          ¿Cómo quieres registrarlos?
        </Alert>

        <div className="mb-3">
          <strong>Padre (ya capturado):</strong>{' '}
          {padre?.snapshot?.item} — Serie {sanitizarSerie(padre?.snapshot?.serial)}
          <div className="ms-3 small text-muted mt-1">
            <div><strong>Accesorios:</strong> {padre?.accesorios || '—'}</div>
            <div><strong>Observación:</strong> {padre?.observacion || '—'}</div>
            <div><strong>Fotos:</strong> {Array.isArray(padre?.fotos) ? padre.fotos.length : 0}</div>
          </div>
        </div>

        <div className="ms-3">
          <strong>Dependiente(s):</strong>
          <ListGroup className="mt-1">
            {hijos.map((h) => (
              <ListGroup.Item key={h._id}>
                <Badge bg="info" className="me-2">🔗</Badge>
                {h.snapshot?.item || 'Item'} — Serie {sanitizarSerie(h.snapshot?.serial)}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>

        <Form.Check
          className="mt-3"
          type="checkbox"
          id="compartir-datos-padre-dep-ingreso"
          label="Los dependientes comparten los mismos accesorios, observación y fotos del padre"
          checked={compartir}
          onChange={(e) => setCompartir(e.target.checked)}
        />
        <div className="small text-muted ms-4">
          Si desmarcas, se abrirá el modal de accesorios para capturar datos de cada dependiente.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-danger" onClick={onCancelar}>
          Cancelar (deshacer padre)
        </Button>
        <Button variant="outline-warning" onClick={onSoloPadre}>
          Solo el padre
        </Button>
        <Button
          variant="primary"
          autoFocus
          onClick={() => (compartir ? onIncluirCompartiendo() : onIncluirPorSeparado())}
        >
          {compartir
            ? 'Incluir con mismos datos (recomendado)'
            : 'Incluir y capturar por separado'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalIncluirDependienteIngreso
